import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

export interface UsageTimelinePoint {
  date: string;
  loginCount: number;
  requestCount: number;
  activeUsers: number;
}

export interface UsageTenantSummary {
  tenantId: string;
  tenantName: string;
  activeUsers: number;
  loginCount: number;
  requestCount: number;
  lastActivity?: string;
}

export interface UsageUserLogin {
  tenantId: string;
  tenantName: string;
  platformUserId: number;
  userName: string;
  email: string;
  loginCount: number;
  webLogins: number;
  mobileLogins: number;
  lastLogin?: string;
}

export interface UsageModule {
  tenantId: string;
  tenantName: string;
  platformUserId: number;
  userName: string;
  email: string;
  module: string;
  client: string;
  requestCount: number;
  activeDays: number;
  lastSeen?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UsageMetricsService {
  private basePath = environment.PLATFORM_API_URL + '/api/usage-metrics';

  private access$: Observable<boolean>;

  constructor(private httpClient: HttpClient) {}

  /** Whether the signed-in user may see usage metrics (decided by the API). Cached per session. */
  hasAccess(): Observable<boolean> {
    if (!this.access$) {
      this.access$ = this.httpClient
        .get<{ allowed: boolean }>(`${this.basePath}/access`)
        .pipe(map(r => !!r && r.allowed), shareReplay(1));
    }
    return this.access$;
  }

  getSummary(from: string, to: string, hideUnknown = false): Observable<UsageTenantSummary[]> {
    return this.httpClient.get<UsageTenantSummary[]>(`${this.basePath}/summary`, { params: this.params(from, to, undefined, hideUnknown) });
  }

  getTimeline(from: string, to: string, tenantId?: string, hideUnknown = false): Observable<UsageTimelinePoint[]> {
    return this.httpClient.get<UsageTimelinePoint[]>(`${this.basePath}/timeline`, { params: this.params(from, to, tenantId, hideUnknown) });
  }

  getLogins(from: string, to: string, tenantId?: string, hideUnknown = false): Observable<UsageUserLogin[]> {
    return this.httpClient.get<UsageUserLogin[]>(`${this.basePath}/logins`, { params: this.params(from, to, tenantId, hideUnknown) });
  }

  getModules(from: string, to: string, tenantId?: string, hideUnknown = false): Observable<UsageModule[]> {
    return this.httpClient.get<UsageModule[]>(`${this.basePath}/modules`, { params: this.params(from, to, tenantId, hideUnknown) });
  }

  private params(from: string, to: string, tenantId?: string, hideUnknown = false): HttpParams {
    let params = new HttpParams().set('from', from).set('to', to);
    if (hideUnknown) {
      params = params.set('hideUnknown', 'true');
    }
    if (tenantId) {
      params = params.set('tenantId', tenantId);
    }
    return params;
  }
}
